import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Package, MapPin, Wallet, Receipt, FolderOpen, Settings,
  ShieldCheck, LogOut, PlusCircle, Search, Bell, Menu, X, Sparkles, CreditCard, Truck, Shield, ArrowLeft,
} from "lucide-react";
import BrandLogo from "./BrandLogo";
import { useAuth } from "../lib/auth";
import ChatWidget from "./ChatWidget";

const navLinks = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Overview" },
  { to: "/dashboard/shipments", icon: Package, label: "Shipments" },
  { to: "/dashboard/smart-services", icon: Sparkles, label: "Smart Services" },
  { to: "/dashboard/addresses", icon: MapPin, label: "Address Book" },
  { to: "/dashboard/wallet", icon: Wallet, label: "Wallet" },
  { to: "/dashboard/billing", icon: Receipt, label: "Billing & Rebills" },
  { to: "/dashboard/billing/methods", icon: CreditCard, label: "Payment Methods" },
  { to: "/dashboard/files", icon: FolderOpen, label: "File Vault" },
  { to: "/dashboard/notifications", icon: Bell, label: "Notifications" },
  { to: "/dashboard/settings", icon: Settings, label: "Settings" },
];

export default function DashboardLayout({ children }) {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // close drawer on route change
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const Sidebar = (variant) => (
    <div className="h-full flex flex-col">
      <div className="px-5 py-5 border-b border-zinc-200 flex items-center justify-between">
        <Link to="/dashboard"><BrandLogo tenant={tenant} /></Link>
        <button
          onClick={() => setSidebarOpen(false)}
          className="md:hidden text-zinc-600 hover:text-zinc-900"
          data-testid="close-sidebar-btn"
        >
          <X size={20} />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navLinks.map(({ to, icon: Icon, label }) => {
          const slug = label.toLowerCase().replace(/[^a-z]+/g, "-");
          return (
            <NavLink
              key={to}
              to={to}
              end={to === "/dashboard"}
              data-testid={`nav-${slug}-${variant}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-all ${
                  isActive ? "brand-bg text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.7} />
              <span>{label}</span>
            </NavLink>
          );
        })}
        {user?.role === "admin" && (
          <>
            <NavLink
              to="/dashboard/admin"
              data-testid={`nav-admin-${variant}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-all ${
                  isActive ? "brand-bg text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                }`
              }
            >
              <ShieldCheck size={16} strokeWidth={1.7} />
              <span>Admin</span>
            </NavLink>
            <NavLink
              to="/dashboard/admin/shipping"
              data-testid={`nav-admin-shipping-${variant}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 pl-9 rounded-sm text-sm transition-all ${
                  isActive ? "brand-bg text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                }`
              }
            >
              <Truck size={14} strokeWidth={1.7} />
              <span>Shipping providers</span>
            </NavLink>
            <NavLink
              to="/dashboard/admin/insurance"
              data-testid={`nav-admin-insurance-${variant}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 pl-9 rounded-sm text-sm transition-all ${
                  isActive ? "brand-bg text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                }`
              }
            >
              <Shield size={14} strokeWidth={1.7} />
              <span>Insurance & risk</span>
            </NavLink>
          </>
        )}
      </nav>
      <div className="px-3 py-4 border-t border-zinc-200">
        <div className="px-3 py-2 text-xs text-zinc-500">
          <div className="font-medium text-zinc-900 truncate">{user?.name}</div>
          <div className="truncate">{user?.email}</div>
          <span className="mt-1 inline-block px-1.5 py-0.5 bg-zinc-100 rounded-sm uppercase tracking-wider text-[10px]">
            {user?.role}
          </span>
        </div>
        <button
          data-testid={`logout-button-${variant}`}
          onClick={async () => { await logout(); navigate("/login"); }}
          className="w-full mt-2 flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm"
        >
          <LogOut size={16} /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-60 bg-white border-r border-zinc-200 z-30" data-testid="dashboard-sidebar">
        {Sidebar("desktop")}
      </aside>

      {/* Mobile drawer */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-sidebar-overlay">
          <div
            onClick={() => setSidebarOpen(false)}
            className="absolute inset-0 bg-zinc-900/50 backdrop-blur-sm"
          />
          <aside className="absolute inset-y-0 left-0 w-[260px] max-w-[85vw] bg-white border-r border-zinc-200 shadow-xl">
            {Sidebar("mobile")}
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="md:ml-60">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-xl border-b border-zinc-200" data-testid="dashboard-topbar">
          <div className="px-4 sm:px-6 md:px-8 h-14 md:h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <button
                data-testid="open-sidebar-btn"
                onClick={() => setSidebarOpen(true)}
                className="md:hidden text-zinc-700 hover:text-zinc-900 p-1"
              >
                <Menu size={22} />
              </button>
              {location.pathname !== "/dashboard" && (
                <button
                  data-testid="back-button"
                  onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/dashboard"))}
                  title="Back"
                  className="text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1 text-sm border border-zinc-200 hover:border-zinc-400 rounded-sm px-2 py-1"
                >
                  <ArrowLeft size={14} />
                  <span className="hidden sm:inline">Back</span>
                </button>
              )}
              <div className="hidden sm:flex items-center gap-2 flex-1 max-w-xl">
                <Search size={16} className="text-zinc-400 shrink-0" />
                <input
                  data-testid="global-search"
                  placeholder="Search shipments, addresses, invoices…"
                  className="flex-1 bg-transparent text-sm focus:outline-none"
                />
              </div>
              <div className="sm:hidden">
                <BrandLogo tenant={tenant} size={24} label={false} />
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-4 shrink-0">
              <div className="hidden md:block text-right">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Wallet</div>
                <div className="font-mono-plex font-semibold text-zinc-900" data-testid="topbar-wallet-balance">
                  ${(user?.wallet_balance || 0).toFixed(2)}
                </div>
              </div>
              <div className="md:hidden text-right" data-testid="topbar-wallet-balance-mobile">
                <div className="font-mono-plex text-xs font-semibold text-zinc-900">
                  ${(user?.wallet_balance || 0).toFixed(0)}
                </div>
              </div>
              <button
                data-testid="topbar-new-shipment"
                onClick={() => navigate("/dashboard/shipments/new")}
                className="brand-bg text-white text-xs md:text-sm px-3 md:px-4 py-1.5 md:py-2 rounded-sm hover:opacity-90 flex items-center gap-1.5 md:gap-2"
              >
                <PlusCircle size={14} />
                <span className="hidden sm:inline">New Shipment</span>
                <span className="sm:hidden">New</span>
              </button>
              <Bell size={16} className="hidden md:block text-zinc-500" />
            </div>
          </div>
        </header>
        <main className="px-4 sm:px-6 md:px-8 py-6 md:py-8">{children}</main>
      </div>
      <ChatWidget />
    </div>
  );
}
